/**
 * Core domain vocabulary for PACE (Plan vs Actual for tool development).
 *
 * A *project* is one part under development (e.g. "Heater Deflector"). It needs one or
 * more press *tools* (Draw, Trimming, Bending …). Every tool passes through the plant's
 * tool-room *stages* (Design → Material → Machining → … → Trial), and the project then
 * passes through project *milestones* (T0 → correction → T1 → T2 → TF → Shipping).
 * Stages and milestones are the same thing in the engine: a dated step in a chain.
 *
 * All dates are plant-local calendar dates as 'YYYY-MM-DD' strings — never epoch
 * values — so "done on 12 May" means the same day on every device.
 */

export const ROLES = ['owner', 'planner', 'mgmt', 'admin'] as const;
export type Role = (typeof ROLES)[number];

/** Where a step lives: on one tool, or on the project as a whole (trials, shipping). */
export const STAGE_KINDS = ['tool', 'project'] as const;
export type StageKind = (typeof STAGE_KINDS)[number];

/** Stored state of a step. */
export const STAGE_STATES = ['open', 'done', 'hold', 'skipped'] as const;
export type StageState = (typeof STAGE_STATES)[number];

/**
 * Computed status of a step (never stored):
 *  - DONE        finished on or before its baseline date
 *  - DONE_LATE   finished after its baseline date
 *  - OVERDUE     open, baseline date has passed
 *  - DUE_TODAY   open, baseline date is today
 *  - AT_RISK     open, not yet due, but the forecast is later than the baseline
 *  - UPCOMING    open, on course
 *  - HOLD        someone put it on hold (with a reason)
 *  - SKIPPED     not needed for this tool (N/A)
 */
export const STEP_STATUSES = [
  'DONE',
  'DONE_LATE',
  'OVERDUE',
  'DUE_TODAY',
  'AT_RISK',
  'UPCOMING',
  'HOLD',
  'SKIPPED',
] as const;
export type StepStatus = (typeof STEP_STATUSES)[number];

/** Project roll-up. Every published project is in exactly one bucket. */
export const PROJECT_STATUSES = ['ON_TRACK', 'AT_RISK', 'LATE', 'SHIPPED'] as const;
export type ProjectStatus = (typeof PROJECT_STATUSES)[number];

export type ProjectPhase = 'draft' | 'active' | 'shipped' | 'cancelled';

/** One dated step as the engine sees it. */
export interface StepInput {
  id: string;
  state: StageState;
  /** Working days after the previous step (or after the project start for the first). */
  days: number;
  /** Current plan (editable while drafting; equals the baseline right after publish). */
  planned: string | null;
  /** Frozen at publish / re-baseline. Null while the project is a draft. */
  baseline: string | null;
  /** Date the owner said it was done. */
  actual: string | null;
}

export interface ToolInput {
  id: string;
  steps: StepInput[];
}

export interface ProjectInput {
  id: string;
  phase: ProjectPhase;
  /** Anchor for the first step of each tool. */
  startDate: string;
  tools: ToolInput[];
  /** Project milestones in order (T0 … Shipping). The first one waits for every tool. */
  milestones: StepInput[];
}

/** Icons available for hold-reason chips (drawn by the UI kit). */
export const REASON_ICONS = [
  'warn',
  'box',
  'tool',
  'truck',
  'swap',
  'scale',
  'repeat',
  'ruler',
  'flask',
  'doc',
  'clock',
  'eye',
  'user',
  'gear',
] as const;
export type ReasonIconName = (typeof REASON_ICONS)[number];
